import React, { useEffect, useState, useRef, useCallback } from 'react';
import axios from '../axiosConfig';
import { Grid, GridColumn, getSelectedState} from '@progress/kendo-react-grid';
import { getter } from "@progress/kendo-react-common";
import { filterBy } from "@progress/kendo-data-query";
import { orderBy } from "@progress/kendo-data-query";
import '@progress/kendo-theme-default/dist/all.scss';
import './GeneralGridStyle.scss';
import SidePanelNewO from './SidePanelNewO';
import useProfileData from '../services/ProfileDataService';
import CustomFilter from '../components/customizedFilters/CustomFilter';
import Loader from './Loader';
import Toast from './ToastNotification';
import Modal from './Modal';
import { ExcelExport } from "@progress/kendo-react-excel-export";
import DraftHoursCellStyle from './customizedColumns/DraftHoursCellStyle';
import { getCurrentLine,subscribeToCurrentLineChanges  } from '../services/CurrentLineService';
import useDebounce from "./../Utilities/useDebounce";
import { useDraftHoursData } from '../Helpers/Hooks/UseGetOrdersData';
import { useQueryClient } from '@tanstack/react-query';
import { GET_DRAFT_HOURS_LIST } from '../Helpers/queries/GetOrdersList';

const DATA_ITEM_KEY = "scheduleId";
const SELECTED_FIELD = "selected";
const idGetter = getter(DATA_ITEM_KEY);

const DraftHoursGrid = ({ columns = [], dateRange,
  filter, setFilter,
  prevFilter, setPrevFilter,
  currentFilter, setCurrentFilter, onRemoveAll,
  setRemoveFilter, dropdownFilters, setDropdownFilters, removeFilter, disabled, 
  exportExcel, setExportExcel, 
  selectedOptions, setSelectedOptions, onDrafthoursEdit,
}) => {

  const [scheduleData, setScheduleData] = useState({ TotalRows: 0, Items: [] });
  const [users, setUsers] = useState([]);  
  const [pageSize, setPageSize] = useState(50);
  const [sort, setSort] = useState([{ field: "ProductionBuildWeek", dir: "asc" }]);
  const [page, setPage] = useState({ skip: 0, take: pageSize });

  const { token } = useProfileData(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [totalRows, setTotalRows] = useState(0);
  const [department, setDepartment] = useState(getCurrentLine().PlantProdFamilyId);

  const [toast, setToast] = useState(null);
  const [idtoast, setIdtoast] = useState(1);  
  const viewAlias = 3;
  const [productName, setProductName] = useState(getCurrentLine().ProdFamilyName);
  const debouncedFilter = useDebounce(filter, "600");
  
  const queryClient = useQueryClient();
  const dateColumn = 'BuildWeek';
  const storedStartDate = null;
  const storedEndDate = null; 
  const plantProdFamilyId = department;
  
  const { data: ordersData, isLoading, refetch} = useDraftHoursData({    
    plantProdFamilyId,    
    dateColumn,
    sort,
    page,
    dateRange,
    storedStartDate,
    storedEndDate,
    filter,
    viewAlias,    
    productName,
    token,
    debouncedFilter,
    setToast,
  });  

  useEffect(() => {
    
    if (ordersData) {               
      setScheduleData({
        TotalRows: ordersData.totalRows,
        Items: ordersData.items,
      });
      
      setTotalRows(ordersData.totalRows);
      setUsers(ordersData.items);
    }
  }, [ordersData]);  

  const [isRefreshing, setIsRefreshing] = useState(false);

  const fetchData = async () => {    
    setIsRefreshing(true);
    await refetch();      
    setIsRefreshing(false);
  };

  //Notice changes on current Line service
  useEffect(() => {
    const unsubscribe = subscribeToCurrentLineChanges(newCurrentLine => {
      setDepartment(newCurrentLine.PlantProdFamilyId);
      setProductName(newCurrentLine.ProdFamilyName);
    });
    return () => {
      unsubscribe();
    };
  }, []);  

  useEffect(() => {
    setToast({ showToast: false });
    setIdtoast(idtoast + 1);
    queryClient.invalidateQueries([GET_DRAFT_HOURS_LIST]);
  }, [token, page.skip, page.take, sort, dateRange, debouncedFilter, department]);

  useEffect(() => {
    setPage({ skip: 0, take: pageSize });
    setCurrentPage(1);
  }, [dateRange, sort, debouncedFilter]);

  const [filtro, setFiltro] = useState(null)

  // **************Static filters approach**************
  const [filterOptions, setFilterOptions] = useState([])

  const filterConfig = {
    text: "text",
    number: "numeric",
    boolean: "boolean",
    date: "date",
    dropdown: {
      filterCell: (props) => {
        const { field } = props;
        return (
          <CustomFilter
            {...props}
            data={users}
            field={field}
            currentFilterField={field}
            onSelectionChange={handleCustomFilterChange}
            onRemoveAll={onRemoveAll}
            selectedOptions2={currentFilter}
            selectedFilter={filtro}
            removeFilter={removeFilter}
            prevFilter={prevFilter}
            filter={dropdownFilters}
            endPointData={filterOptions}
            viewOption={viewAlias}
          />
        );
      }
    }
  };
  
  const handleCustomFilterChange = (selectedOptions, currentFilterField) => {
    
    setFilter((prevFilter) => {
      if (!prevFilter) {
        // Si no hay un filtro existente, crea uno nuevo con la selección actual
        return {
          logic: "or",
          filters: [
            ...selectedOptions.map((option) => ({
              field: currentFilterField,
              operator: "contains",
              value: option,
            })),
          ],
        };
      } else {
        // Si ya existe un filtro, actualízalo con la selección actual
        const updatedFilters = [
          ...prevFilter.filters.filter((filter) => filter.field !== currentFilterField),
          ...selectedOptions.map((option) => ({
            field: currentFilterField,
            operator: "contains",
            value: option,
          })),
        ];

        return {
          ...prevFilter,
          filters: updatedFilters,
        };
      }
    });
    setFiltro(currentFilterField);
    setCurrentFilter(selectedOptions);
    setRemoveFilter(false);
    const allFields = filter?.filters?.map(filter => filter.field) || [];
    allFields.unshift(currentFilterField);
    const allFields2 = [...new Set(allFields)];
    setPrevFilter(allFields2);
    setDropdownFilters(filter?.filters);
  };


  const pageChange = (event) => {
    const targetEvent = event.targetEvent;
    const newPage = {
      skip: event.page.skip,
      take: targetEvent.value === 'All' ? totalRows : event.page.take,
    };

    setPage(newPage);
    setCurrentPage(newPage.skip / pageSize + 1);
  };

  const handleFilterChange = (event) => {
    setFilter(event.filter);
  };

  const filteredUsers = filterBy(users, filter);
  const sortedUsers = orderBy(filteredUsers, sort);

  const [selectedColumns, setSelectedColumns] = useState(columns);

  useEffect(() => {
    setSelectedColumns(columns);
  }, [columns], [sortedUsers]);

  const [selectedColumnFields, setSelectedColumnFields] = useState(columns.map(column => column.field));

  useEffect(() => {
    setSelectedColumnFields(columns.map(column => column.field));
  }, [columns]);

  const [openForm, setOpenForm] = React.useState(false);
  const [editItem, setEditItem] = React.useState();
  const [data, setData] = React.useState(sortedUsers);

  const enterEdit = async (item) => {
    
    const updatedItem = {
      ...item,
      orderNumber: 0,
      orderNumberChecker: 0,
    };

    setOpenForm(true);
    setEditItem(updatedItem);

};

  const handleDesignerDetailHrsChange = (newDesignerDetailHrs,) => {
    setEditItem((prevItem) => ({
      ...prevItem,
      orderNumber: newDesignerDetailHrs      
    }));
  };

  const handleDesignerCheckerHrsChange = (newDesignerCheckerHrs) => {
    setEditItem((prevItem) => ({
      ...prevItem,
      orderNumberChecker: newDesignerCheckerHrs
    }));
  };


  const handleSubmit = (event) => {
    let newData = data.map((item) => {
      if (event.item === item.item) {
        item = {
          ...event,
        };
      }
      return item;
    });
    setData(newData);



    setOpenForm(false);
  };

  const [profileImgAssigned, setProfileImgAssigned] = useState();
  const [designerName, setName] = useState();
  const [modalVisible, setModalVisible] = useState(false);

  const handleCancelEdit = () => {
    setOpenForm(false);
  };

  const handleSortChange = (e) => {
    setSort(e.sort || []);
  };

  useEffect(() => {
    setPageSize(page.take);
  }, [page]);

  const _export = React.useRef(null);
  const _grid = React.useRef();

  const _formattedDate = (() => {
    const today = new Date();
    const month = (today.getMonth() + 1).toString().padStart(2, '0');
    const day = today.getDate().toString().padStart(2, '0');
    const year = today.getFullYear();
    return `${month}-${day}-${year}`;
  })();

  const excelExport = () => {
    if (_export.current !== null && exportExcel === true) {      

      const today = new Date();
      // _export.current.save(users, _grid.current.columns);
      _export.current.save(users);
    }
  };
  
  useEffect(() => {
    excelExport();
    setExportExcel(false);
  }, [exportExcel === true]);

  // Zoom adjustment
  const [zoomLevel, setZoomLevel] = useState(1);
  // TODO: To use Css calc() for handle the hight instead
  const [gridHeight, setGridHeight] = useState(window.innerHeight - 270);

  useEffect(() => {
    const handleResize = () => {
      const viewportWidth = window.innerWidth;
      const windowWidth = window.outerWidth;
      const newZoomLevel = windowWidth / viewportWidth;
      setZoomLevel(newZoomLevel);

      const newGridHeight = window.innerHeight - 270;
      setGridHeight(newGridHeight);
    };

    handleResize();
    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, []);
  // Zoom adjustment


  // Handle options selected in the selection column
  const [dataState, setDataState] = useState([]);
  useEffect(() => {
    if (users) {
      if(users.length > 0)
      {
        const updatedDataState = users.map((dataItem) =>
          Object.assign(
            {
              selected: false,
            },
            dataItem
          )
        );
        setDataState(updatedDataState);
      }
      else
      {
        setDataState([]);
      }
    }
  }, [users]);
  
  const [selectedState, setSelectedState] = useState({});
  const onSelectionChange = useCallback(
    (event) => {
      const newSelectedState = getSelectedState({
        event,
        selectedState: selectedState,
        dataItemKey: DATA_ITEM_KEY,
      });
      setSelectedState(newSelectedState);
    },
    [selectedState]
  );

  const onHeaderSelectionChange = useCallback((event) => {
    const checkboxElement = event.syntheticEvent.target;
    const checked = checkboxElement.checked;
    const newSelectedState = {};
    event.dataItems.forEach((item) => {
      newSelectedState[idGetter(item)] = checked;
    });
    setSelectedState(newSelectedState);
  }, []);

  useEffect(() => {
    const selectedItems = dataState.filter(item => selectedState[item[DATA_ITEM_KEY]]); 
    setSelectedOptions(selectedItems); //selectedItems stores the selected options information
  }, [selectedState, dataState]);
  // Handle options selected in the selection column

  // Update the grid when the form is submitted
  const showToastSuccess = async () => {
    setIdtoast(idtoast + 1);
    setToast({ showToast: false });
    setToast({ showToast: true, message: 'Drafting week(s) updated successfully', type: 'success' });
  };

  useEffect(() => {
    showToastSuccess();
    setSelectedState({});
    fetchData();
  }, [onDrafthoursEdit]);
  // Update the grid when the form is submitted

  return (
    <div style={{ position: 'relative' }}>  
      {(isLoading || isRefreshing) && (
        <Loader></Loader>
      )}

      <Modal
        visible={modalVisible}
        onClose={() => setModalVisible(false)}
        title='Oops! It looks like this task was already taken'
        message={designerName + ' took this task'}
        type='TAKENORDER'
        image= {profileImgAssigned}
      />

      {toast && <Toast key={idtoast} showToast={toast.showToast} message={toast.message} type={toast.type} />}

      <div style={{ height: 'calc(100vh - 290px)', marginBottom: '15px', position: 'relative' }}>
        <ExcelExport ref={_export} fileName={`drafhours&capacity_${_formattedDate}.xlsx`}>
          <Grid
            className='roboto-15-400'
            // data={(users || [])}
            data={dataState.length > 0 ? dataState.map((item) => ({
              ...item,
              [SELECTED_FIELD]: selectedState[idGetter(item)],
            })) : []}
            // dataItemKey={DATA_ITEM_KEY}
            selectedField={SELECTED_FIELD}
            selectable={{
              enabled: true,
              drag: false,
              cell: false,
              mode: "multiple",
            }}
            onSelectionChange={onSelectionChange}
            onHeaderSelectionChange={onHeaderSelectionChange}
            skip={page.skip}
            take={page.take}
            total={totalRows}
            pageable={{
              buttonCount: 4,
              type: "input",
              pageSizes: [50, 150, 200, totalRows].filter(num => num <= totalRows),
              pageSize: pageSize,
            }}
            onPageChange={pageChange}
            sortable={true}
            filterable={true}
            filter={filter}
            onFilterChange={handleFilterChange}
            resizable
            reorderable
            sort={sort}
            onSortChange={handleSortChange}
            style={{
              borderRadius: '8px',
              display: 'flex',
              margin: '15px',
              height: 'inherit'
            }}
            ref={_grid}
          >
            {/* <GridColumn
              className='roboto-15-400'
              reorderable={false}
              sortable={false}
              filterable={false}
              editable={false}
              field="rowIndex"
              title="#"
              width="40px"
            /> */}
            <GridColumn
              filterable={false}
              // locked={true}
              editable={false}
              field={SELECTED_FIELD}
              width="43px"
              headerSelectionValue={
                dataState.length > 0 && dataState.findIndex((item) => !selectedState[idGetter(item)]) === -1
              }             
            />
            {selectedColumns
              .filter((column) => column.visible)
              .map((column) => {
                return (
                    <GridColumn
                      className='roboto-15-400'
                      sortable={true}
                      key={column.field}
                      field={column.field}
                      title={column.title}
                      headerCell={column.header}
                      width={column.minWidth}
                      filter={filterConfig[column.filterType]}
                      format="{0:d}"
                      filterCell={filterConfig[column.filterType].filterCell}
                      cell={(props) => (
                        <DraftHoursCellStyle
                          {...props}
                        />
                      )}
                    />
                  );
                }
              )}
          </Grid>
        </ExcelExport>
      </div>

      {openForm && editItem && editItem.orderDetailId && (
        <SidePanelNewO
          cancelEdit={handleCancelEdit}          
          item={editItem}
          disabled= {true}
        />
      )}  
    </div>
  );
}

export default DraftHoursGrid;